import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db, auth } from '../../firebaseConfig';
import { doc, collection, getDoc, getDocs, addDoc, updateDoc, query, orderBy } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import '../../styles/NotificationDetail.css';

const NotificationDetail = () => {
  const { id } = useParams(); // URLからIDを取得
  const [user] = useAuthState(auth);
  const [relatedInquiry, setRelatedInquiry] = useState(null);
  const [generalNotification, setGeneralNotification] = useState(null);
  const [specificNotification, setSpecificNotification] = useState(null);
  const [responses, setResponses] = useState([]);
  const [newResponse, setNewResponse] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotificationDetails = async () => {
      try {
        if (user) {
          // notifications/{id} ドキュメントを取得
          const notificationRef = doc(db, `members/${user.uid}/notifications`, id);
          const notificationSnap = await getDoc(notificationRef);

          if (notificationSnap.exists()) {
            const notificationData = notificationSnap.data();
            await updateDoc(notificationRef, { read: true });
            if (notificationData.judge === 'inq') {
              // inquiries/{id} ドキュメントを取得
              const inquiryRef = doc(db, 'inquiries', id);
              const inquirySnap = await getDoc(inquiryRef);

              if (inquirySnap.exists()) {
                const inquiryData = inquirySnap.data();
                setRelatedInquiry({ id: inquirySnap.id, ...inquiryData });

                // inquiries/{id}/responses サブコレクションを取得
                const responsesRef = query(
                  collection(db, `inquiries/${id}/responses`),
                  orderBy('createdAt', 'asc') // 日付順にソート
                );
                const responsesSnap = await getDocs(responsesRef);
                setResponses(
                  responsesSnap.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                  }))
                );
              } else {
                console.error('Inquiry not found!');
              }
            } else if (notificationData.judge === 'gen') {
              // general_notifications/{id} ドキュメントを取得
              const generalNotificationRef = doc(db, 'general_notifications', id);
              const generalNotificationSnap = await getDoc(generalNotificationRef);

              if (generalNotificationSnap.exists()) {
                const generalNotificationData = generalNotificationSnap.data();
                setGeneralNotification({ id: generalNotificationSnap.id, ...generalNotificationData });
              } else {
                console.error('General notification not found!');
              }
            } else if (notificationData.judge === 'spe') {
              // specific_notifications/{id} ドキュメントを取得
              const specificNotificationRef = doc(db, 'specific_notifications', id);
              const specificNotificationSnap = await getDoc(specificNotificationRef);

              if (specificNotificationSnap.exists()) {
                const specificNotificationData = specificNotificationSnap.data();
                setSpecificNotification({ id: specificNotificationSnap.id, ...specificNotificationData });
              } else {
                console.error('Specific notification not found!');
              }
            } else {
              console.log(`Unsupported judge value: ${notificationData.judge}`);
            }
          } else {
            console.error('Notification not found!');
          }
        }
      } catch (error) {
        console.error('Error fetching notification and related data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotificationDetails();
  }, [id, user]);

  const handleAddResponse = async () => {
    if (!newResponse.trim()) return;

    try {
      const inquiryRef = doc(db, 'inquiries', id); // inquiries/{id} の参照

      // inquiries/{id}/responses サブコレクションに返信を追加
      const responseRef = collection(db, `inquiries/${id}/responses`);
      await addDoc(responseRef, {
        userId: user?.uid, // ユーザーIDを保存
        sender: {
          uid: user?.uid,
          email: user?.email,
          displayName: user?.displayName || 'Unknown User', // ユーザーの名前を保存
        }, // 送信者情報
        content: newResponse,
        createdAt: new Date(),
      });

      // inquiries/{id} の status を pending に更新
      await updateDoc(inquiryRef, {
        status: 'pending',
      });

      // フロントエンドの状態を更新
      setResponses((prev) => [
        ...prev,
        {
          userId: user?.uid,
          sender: {
            uid: user?.uid,
            email: user?.email,
            displayName: user?.displayName || 'Unknown User',
          },
          content: newResponse,
          createdAt: new Date(),
        },
      ]);
      setNewResponse('');
    } catch (error) {
      console.error('Error adding response:', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (relatedInquiry) {
    return (
      <div className="notification-detail-container">
        <div className="header">
          <Link to="/settings" className="back-link-cont">←</Link>
          <p className="settings-title-cont">お問い合わせ詳細</p>
        </div>
        <h2 className="inquiry-title">{relatedInquiry.title}</h2>
        <span className="notification-date">
          {new Date(relatedInquiry.createdAt.toDate()).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>
        <div className="inquiry-content">
          <h3>お問い合わせ内容</h3>
          <p style={{ whiteSpace: 'pre-wrap' }}>{relatedInquiry.content}</p>
        </div>
<div className="responses-section">
  <h3>返信一覧</h3>
  {responses.length > 0 ? (
    responses.map((response) => {
      // createdAt を安全に処理
      const createdAt =
        response.createdAt?.toDate?.() || // Firestore Timestamp の場合
        new Date(response.createdAt);    // Date または文字列の場合

      return (
        <div key={response.id} className="response-item">
          <p>
            <strong>
              {response.sender === 'admin'
                ? '管理者'
                : response.sender?.displayName || 'Unknown'}
              :
            </strong>{' '}
            {response.content}
          </p>
          <span className="response-date">
            {createdAt.toLocaleDateString('ja-JP', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
        </div>
      );
    })
  ) : (
    <p>返信はまだありません。</p>
  )}
  <textarea
    value={newResponse}
    onChange={(e) => setNewResponse(e.target.value)}
    placeholder="返信を入力してください"
    rows="4"
    className="response-textarea"
  />
  <button onClick={handleAddResponse} className="add-response-button">返信を送信</button>
</div>


      </div>
    );
  }

  if (generalNotification) {
    return (
      <div className="notification-detail-container">
        <div className="header">
          <Link to="/settings" className="back-link-cont">←</Link>
          <p className="settings-title-cont">お知らせ詳細</p>
        </div>
        <h2>{generalNotification.title}</h2>
        <span className="notification-date">
          {new Date(generalNotification.createdAt.toDate()).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>
        <p style={{ whiteSpace: 'pre-wrap' }}>{generalNotification.content}</p>
      </div>
    );
  }

  if (specificNotification) {
    return (
      <div className="notification-detail-container">
        <div className="header">
          <Link to="/settings" className="back-link-cont">←</Link>
          <p className="settings-title-cont">特定のお知らせ詳細</p>
        </div>
        <h2>{specificNotification.title}</h2>
        <span className="notification-date">
          {new Date(specificNotification.createdAt.toDate()).toLocaleDateString('ja-JP', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </span>
        <p style={{ whiteSpace: 'pre-wrap' }}>{specificNotification.content}</p>
      </div>
    );
  }

  return <div>No data found for the provided ID.</div>;
};

export default NotificationDetail;
